import { Typography, TextField, Box, Alert } from '@mui/material';
import { useState, useEffect } from 'react';
import EntityDetailPopup from '../../utility/EntityDetailPopup';
import apiService from '../../../api/apiService';
import { popupClasses } from '../../utility';

export default function EditFarmAliasPopup(props) {
  const { open, onClose, values = [], currDarkThemeClass, handleSetValues, init } = props;

  const [farmAliases, setFarmAliases] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (values && values.length > 0) {
      const initialAliases = values.map(farm => ({
        id: farm.id,
        farm_name: farm.farm_name,
        farm_alias: farm.farm_alias,
        originalAlias: farm.farm_alias,
      }));
      setFarmAliases(initialAliases);
    }
  }, [values]);

  // Handle alias change for a specific farm
  const handleAliasChange = (farmId, newAlias) => {
    setFarmAliases(prev => {
      const updated = prev.map(farm => (farm.id === farmId ? { ...farm, farm_alias: newAlias } : farm));

      // Check if there are any changes from original values
      const hasAnyChanges = updated.some(farm => farm.farm_alias !== farm.originalAlias);
      setHasChanges(hasAnyChanges);

      return updated;
    });

    if (errors[farmId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[farmId];
        return newErrors;
      });
    }
  };

  const validateAliases = () => {
    const newErrors = {};
    const aliasCount = {};

    farmAliases.forEach(farm => {
      const alias = farm.farm_alias.trim();
      if (alias) {
        aliasCount[alias] = (aliasCount[alias] || 0) + 1;
      }
    });

    farmAliases.forEach(farm => {
      const alias = farm.farm_alias.trim();

      if (!alias) {
        newErrors[farm.id] = 'Alias cannot be empty';
      } else if (aliasCount[alias] > 1) {
        newErrors[farm.id] = 'Alias must be unique';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateFarmAliases = async () => {
    if (!validateAliases()) {
      return;
    }

    try {
      const response = await apiService.modifyInstance('farm/', farmAliases);
      if (response && response.status === 200) {
        console.log('Farm aliases updated successfully:', response.data);

        if (handleSetValues) {
          const updatedFarms = values.map(farm => {
            const updatedAlias = farmAliases.find(alias => alias.id === farm.id);
            return updatedAlias ? { ...farm, farm_alias: updatedAlias.farm_alias } : farm;
          });
          handleSetValues('farms', updatedFarms);
        }

        if (init) {
          await init();
        }

        setHasChanges(false);
        onClose();
      } else {
        console.error('Error updating farm aliases:', response);
      }
    } catch (error) {
      console.error('Error updating farm aliases:', error);
    }
  };

  const entityDetailPopupProps = {
    open: open,
    onClose: onClose,
    modalMaxWidth: '980px',
    currDarkThemeClass: currDarkThemeClass,
    title: 'Edit Farm Aliases',
    onSaveClick: handleUpdateFarmAliases,
    headContentText: 'Edit the aliases for the farms. Each farm must have a unique alias.',
    saveDisabled: !hasChanges || Object.keys(errors).length > 0,
  };

  const mynewProps = {
        className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
  }

  return (
    <EntityDetailPopup {...entityDetailPopupProps}>
      <Box sx={{ padding: '20px 0' }}>
        {farmAliases.length === 0 ? (
          <Typography>No farms available to edit.</Typography>
        ) : (
          <>
            {hasChanges && (
              <Alert severity="info" sx={{ marginTop: 2 }}>
                You have unsaved changes.
              </Alert>
            )}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                Please fix the errors below before saving.
              </Alert>
            )}

            {farmAliases.map((farm, index) => (
              <Box
                key={farm.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  // marginBottom: 2,
                  // padding: '12px',
                }}
              >
                <Box sx={{ flex: 1, marginRight: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: currDarkThemeClass ? '#ffffff' : '#333333',
                    }}
                  >
                    {farm.farm_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: currDarkThemeClass ? '#cccccc' : '#666666',
                      fontSize: '0.875rem',
                    }}
                  >
                    License: {values.find(v => v.id === farm.id)?.license_number || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ width: '200px'}}>
                  <TextField
                    label="Farm Alias"
                    value={farm.farm_alias}
                    onChange={e => handleAliasChange(farm.id, e.target.value)}
                    error={!!errors[farm.id]}
                    helperText={errors[farm.id]}
                    size="small"
                    fullWidth
                    variant="outlined"
                    {...mynewProps}
                  />
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>
    </EntityDetailPopup>
  );
}
